import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getPlatformFee, savePlatformFee } from "store/platform-fees"
import { toaster } from "components/common/Toast"

export const FEE_PAYER_MODE = {
  GUEST_PAYS: "guest_pays",
  GUIDE_PAYS: "guide_pays",
  SPLIT: "split"
}

export const SPLIT_TYPE = {
  PERCENTAGE: "percentage",
  FIXED: "fixed"
}

const DEFAULT_GUIDE_PERCENTAGE = "50"
const DEFAULT_GUIDE_FIXED = ""

export function usePlatformFees({ enabled = false } = {}) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const platformFeeData = useSelector((state) => state.platformFees?.data)
  const isLoading = useSelector((state) => state.platformFees?.loading || false)
  const isSaving = useSelector((state) => state.platformFees?.save?.loading || false)

  const [feePayerMode, setFeePayerMode] = useState(FEE_PAYER_MODE.GUEST_PAYS)
  const [splitType, setSplitType] = useState(SPLIT_TYPE.PERCENTAGE)
  const [percentagePortion, setPercentagePortion] = useState(DEFAULT_GUIDE_PERCENTAGE)
  const [fixedPortion, setFixedPortion] = useState(DEFAULT_GUIDE_FIXED)
  const [guidePortionError, setGuidePortionError] = useState(null)
  const [platformFeeAsLineItem, setPlatformFeeAsLineItem] = useState(false)

  useEffect(() => {
    if (enabled) dispatch(getPlatformFee())
  }, [dispatch, enabled])

  useEffect(() => {
    if (!platformFeeData) return

    const mode = platformFeeData.fee_payer_mode || FEE_PAYER_MODE.GUEST_PAYS
    setFeePayerMode(mode)

    if (mode === FEE_PAYER_MODE.SPLIT) {
      const type = platformFeeData.split_type || SPLIT_TYPE.PERCENTAGE
      setSplitType(type)
      const portion = platformFeeData.guide_portion != null ? String(platformFeeData.guide_portion) : null
      if (type === SPLIT_TYPE.PERCENTAGE) {
        setPercentagePortion(portion ?? DEFAULT_GUIDE_PERCENTAGE)
      } else {
        setFixedPortion(portion ?? DEFAULT_GUIDE_FIXED)
      }
    }

    setPlatformFeeAsLineItem(!!platformFeeData.show_as_line_item)
  }, [platformFeeData])

  const guidePortion = splitType === SPLIT_TYPE.PERCENTAGE ? percentagePortion : fixedPortion
  const rawSetGuidePortion = splitType === SPLIT_TYPE.PERCENTAGE ? setPercentagePortion : setFixedPortion

  const getGuidePortionError = (val, type) => {
    const num = parseFloat(val)
    if (!val || val === "" || num <= 0) return t("settings.tax_price_greater_than_zero")
    if (type === SPLIT_TYPE.PERCENTAGE && num >= 100) return t("settings.tax_price_max_percentage")
    const decimalMatch = String(val).match(/\.(\d+)/)
    if (decimalMatch && decimalMatch[1].length > 2) return t("settings.tax_price_max_two_decimals")
    return null
  }

  const setGuidePortion = (val) => {
    rawSetGuidePortion(val)
    setGuidePortionError(getGuidePortionError(val, splitType))
  }

  const handleSetSplitType = (type) => {
    setSplitType(type)
    setGuidePortionError(null)
  }

  const handleSetFeePayerMode = (mode) => {
    setFeePayerMode(mode)
    setGuidePortionError(null)
  }

  const guideNum = parseFloat(guidePortion) || 0
  const guestPortion = splitType === SPLIT_TYPE.PERCENTAGE && !guidePortionError ? 100 - guideNum : null

  const handleTogglePlatformFeeLineItem = (checked) => {
    setPlatformFeeAsLineItem(checked)
  }

  const handleSaveChanges = async () => {
    try {
      const payload = { fee_payer_mode: feePayerMode, show_as_line_item: platformFeeAsLineItem }

      if (feePayerMode === FEE_PAYER_MODE.SPLIT) {
        const error = getGuidePortionError(guidePortion, splitType)
        if (error) {
          setGuidePortionError(error)
          return
        }
        payload.split_type = splitType
        payload.guide_portion = guideNum
        if (splitType === SPLIT_TYPE.PERCENTAGE) {
          payload.guest_portion = guestPortion
        }
      }

      await dispatch(savePlatformFee(payload))
    } catch (error) {
      console.error(error || "Failed to save settings")
    }
  }

  return {
    feePayerMode,
    setFeePayerMode: handleSetFeePayerMode,
    splitType,
    setSplitType: handleSetSplitType,
    guidePortion,
    setGuidePortion,
    guidePortionError,
    guestPortion,
    isLoading,
    isSaving,
    handleSaveChanges,
    platformFeeAsLineItem,
    handleTogglePlatformFeeLineItem
  }
}
